<template>
  <section class="step-digit-code__step">
    <div class="step-digit-code__wrapper">
      <div class="step-digit-code__digits-wrapper">
        <input
          v-for="n in digits"
          :class="[
            'step-digit-code__digit',
            n === currentDigitPosition && !isDigitsCompleted && 'step-digit-code__digit--active',
          ]"
          :key="`digit-${n}`"
          :id="n"
          :ref="`ga-digit-${n}`"
          @input="moveNextDigit($event)"
          @paste="onPaste"
          @keydown="backspaceDigit"
          :value="currentDigits[n - 1]"
          type="number"
          max="1"
        />
      </div>
    </div>
    <div class="step-digit-code__actions-group">
      <button
        v-if="!isLoading"
        class="btn btn--primary step-digit-code__confirm-btn"
        @click="submitCode()"
        @keydown="backspaceDigit"
        ref="confirm"
        :disabled="isSaveDisabled"
      >
        {{ confirmBtnLabel }}
      </button>
      <ActionLoader v-else class="step-digit-code__loader" />
    </div>
  </section>
</template>

<script>
import ActionLoader from '@/components/shared/ActionLoader.vue'

export default {
  name: 'StepDigitCode',
  components: {
    ActionLoader,
  },
  data() {
    return {
      isSaveDisabled: true,
      isDigitsCompleted: false,
      isLoading: false,
      currentDigitPosition: 1,
      currentDigits: [],
      digits: 6,
    }
  },
  watch: {
    currentDigitPosition() {
      const fullilledDigits = this.currentDigits?.filter(digit => digit !== null).length
      const isDigitsCompleted = fullilledDigits === this.digits
      this.isDigitsCompleted = isDigitsCompleted
      this.isSaveDisabled = !isDigitsCompleted
    },
  },
  computed: {
    confirmBtnLabel() {
      return 'Confirm code'
    },
  },
  methods: {
    backspaceDigit($event) {
      if ($event.keyCode === 8) {
        const targetNumber = Number($event.currentTarget.id)
          ? Number($event.currentTarget.id) - 1
          : this.digits
        const currentDigitIndex = targetNumber || 1
        this.currentDigits[currentDigitIndex] = null
        this.$refs[`ga-digit-${currentDigitIndex}`][0].focus()
        this.currentDigitPosition = currentDigitIndex
      }
    },
    moveNextDigit($event) {
      if ($event && $event.inputType === 'insertFromPaste') {
        $event.preventDefault()
        return
      }
      const currentDigit = $event.currentTarget.value
      const currentDigitPosition = Number($event.currentTarget.id)
      this.$refs[`ga-digit-${currentDigitPosition}`][0].value = ''
      this.currentDigitPosition = currentDigitPosition
      if (currentDigit.length > 1) currentDigit.substr(0, 1)
      const currentDigitIndex = this.currentDigitPosition - 1
      const isValid = /\d/.test(currentDigit)
      this.currentDigits[currentDigitIndex] = isValid ? currentDigit : null
      if (!isValid) return
      if (this.currentDigits[currentDigitIndex]?.length > 1) {
        this.currentDigits[currentDigitIndex] = this.currentDigits[currentDigitIndex][1]
      }
      this.currentDigitPosition++
      if (this.currentDigitPosition < this.digits + 1) {
        this.$refs[`ga-digit-${this.currentDigitPosition}`][0].focus()
      } else {
        this.$nextTick(() => this.$refs.confirm.focus())
      }
    },
    onPaste($event) {
      const pastedData = $event.clipboardData.getData('text')?.replace(/\D/g, '')
      if (pastedData) {
        const numbers = pastedData.split('')
        for (let index = 0; index < this.digits; index++) {
          this.$refs[`ga-digit-${index + 1}`][0].value = numbers[index] ? numbers[index] : null
        }
        $event.preventDefault()
        this.currentDigits = numbers.slice(0, 6)
        this.currentDigitPosition = this.currentDigits.length + 1
        if (this.currentDigitPosition < 6)
          this.$refs[`ga-digit-${this.currentDigitPosition}`][0]?.focus()
      }
      if (this.currentDigitPosition === this.digits + 1) {
        this.$nextTick(() => this.$refs.confirm.focus())
      }
    },
    async submitCode() {
      this.isLoading = true
      this.$parent.$emit('is-digit-code-loading', true)
      this.isSaveDisabled = true
      const code = this.currentDigits.join('')
      try {
        await this.$store.dispatch('auth/submitMFACode', code)
        this.$emit('handle-success-form')
      } catch (e) {
        this.currentDigits = []
        this.$emit('is-digit-code-loading', false)
        this.$parent.$emit('is-digit-code-loading', false)
        this.isLoading = false
        this.$nextTick(() => this.$refs[`ga-digit-1`][0].focus())
        this.currentDigitPosition = 1
      }
    },
  },
  async mounted() {
    this.$refs[`ga-digit-1`][0].focus()
  },
}
</script>

<style lang="scss" scoped>
.step-digit-code {
  &__step {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  &__digits-wrapper {
    display: flex;
    justify-content: center;
  }
  &__digit {
    width: 41px;
    height: 57px;
    border-radius: 10px;
    margin-right: 10px;
    padding: 11px;
    font-size: rem(24);
    font-family: var(--font-regular);
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    &[type='number'] {
      -moz-appearance: textfield;
    }
    &:last-child {
      margin-right: 0;
    }
    &--active {
      border: 1px solid var(--chetwode-blue);
      box-shadow: 0px 0px 3px 2px var(--chetwode-blue);
    }
  }
  &__wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  &__input {
    max-width: 100%;
    input {
      height: 38px;
      padding: 11px;
      @include mobile {
        height: 46px;
      }
    }
  }
  &__confirm-btn {
    margin: 0;
  }
  &__back-btn {
    margin: 20px 0 50px;
  }
  &__actions-group {
    margin-top: 30px;
    text-align: end;
  }
  &__loader {
    margin-right: 50px;
  }
}
</style>
